import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useBlockingMutation } from '../hooks/useBlockingMutation';
import { Window, Button, Select, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@warmancer/ui';
import { errorsApi, type ErrorLog } from '../api/client';
import { useAdminStore } from '../stores/adminStore';
import { formatTimestamp } from '../utils/formatTimestamp';
import styles from './Errors.module.css';

const sourceOptions = [
  { value: '', label: 'All Sources' },
  { value: 'web', label: 'Web' },
  { value: 'admin-web', label: 'Admin Web' },
];

const levelOptions = [
  { value: '', label: 'All Levels' },
  { value: 'error', label: 'Error' },
  { value: 'warn', label: 'Warning' },
  { value: 'info', label: 'Info' },
];

const resolvedOptions = [
  { value: '', label: 'All' },
  { value: 'false', label: 'Unresolved' },
  { value: 'true', label: 'Resolved' },
];

export function Errors() {
  const queryClient = useQueryClient();
  const { hasRole } = useAdminStore();

  const [source, setSource] = useState('');
  const [level, setLevel] = useState('');
  const [resolved, setResolved] = useState('');
  const [page, setPage] = useState(0);
  const [selectedError, setSelectedError] = useState<ErrorLog | null>(null);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [exportError, setExportError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const limit = 25;

  const { data: stats } = useQuery({
    queryKey: ['error-stats'],
    queryFn: () => errorsApi.getStats(),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['errors', { source, level, resolved, limit, offset: page * limit }],
    queryFn: () =>
      errorsApi.list({
        source: source || undefined,
        level: level || undefined,
        resolved: resolved === '' ? undefined : resolved === 'true',
        limit,
        offset: page * limit,
      }),
  });

  const resolveMutation = useBlockingMutation({
    loadingMessage: 'Updating error...',
    mutationFn: ({ id, resolved }: { id: string; resolved: boolean }) =>
      errorsApi.resolve(id, resolved),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['errors'] });
      queryClient.invalidateQueries({ queryKey: ['error-stats'] });
    },
  });

  const deleteMutation = useBlockingMutation({
    loadingMessage: 'Deleting error...',
    mutationFn: (id: string) => errorsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['errors'] });
      queryClient.invalidateQueries({ queryKey: ['error-stats'] });
      setSelectedError(null);
    },
  });

  const purgeMutation = useBlockingMutation({
    loadingMessage: 'Purging resolved errors...',
    mutationFn: () =>
      errorsApi.purge({
        source: source || undefined,
        level: level || undefined,
        resolved: resolved || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['errors'] });
      queryClient.invalidateQueries({ queryKey: ['error-stats'] });
      setShowPurgeConfirm(false);
      setPage(0);
    },
  });

  const downloadJson = useCallback((jsonData: unknown, filename: string) => {
    const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const handleExportPage = useCallback(() => {
    try {
      if (!data?.errors) return;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      downloadJson(data.errors, `warmancer-errors-page-${timestamp}.json`);
    } catch {
      setExportError('Failed to export errors');
    }
  }, [data?.errors, downloadJson]);

  const handleExportAll = useCallback(async () => {
    try {
      const result = await errorsApi.exportAll({
        source: source || undefined,
        level: level || undefined,
        resolved: resolved || undefined,
      });
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      downloadJson(result.errors, `warmancer-errors-all-${timestamp}.json`);
    } catch {
      setExportError('Failed to export errors');
    }
  }, [source, level, resolved, downloadJson]);

  const totalPages = Math.ceil((data?.pagination.total || 0) / limit);

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Error Logs</h1>

      <div className={styles.statsRow}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Total</span>
          <span className={styles.statValue}>{stats?.total || 0}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Unresolved</span>
          <span className={`${styles.statValue} ${styles.statWarning}`}>
            {stats?.unresolved || 0}
          </span>
        </div>
      </div>

      <div className={styles.filtersRow}>
        <div className={styles.filters}>
          <Select
            name="source"
            options={sourceOptions}
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
              setPage(0);
            }}
          />
          <Select
            name="level"
            options={levelOptions}
            value={level}
            onChange={(e) => {
              setLevel(e.target.value);
              setPage(0);
            }}
          />
          <Select
            name="resolved"
            options={resolvedOptions}
            value={resolved}
            onChange={(e) => {
              setResolved(e.target.value);
              setPage(0);
            }}
          />
        </div>
        <div className={styles.bulkActions}>
          {hasRole('mod') && (
            <button
              className={styles.actionBtn}
              onClick={() => setShowPurgeConfirm(true)}
            >
              Purge All
            </button>
          )}
          {exportError && (
            <span style={{ color: '#ff4444', fontSize: 12 }}>{exportError}</span>
          )}
          <button
            className={styles.actionBtn}
            onClick={handleExportPage}
            disabled={!data?.errors?.length}
          >
            Export Page
          </button>
          <button
            className={styles.actionBtn}
            onClick={handleExportAll}
          >
            Export All
          </button>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Timestamp</TableHeader>
              <TableHeader>Source</TableHeader>
              <TableHeader>Level</TableHeader>
              <TableHeader>Message</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.errors.map((error) => (
              <TableRow key={error.id}>
                <TableCell>
                  <span className={styles.timestamp}>
                    {formatTimestamp(error.created_at)}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={styles.source}>{error.source}</span>
                </TableCell>
                <TableCell>
                  <span className={`${styles.level} ${styles[`level${error.level}`]}`}>
                    {error.level}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={styles.message} title={error.message}>
                    {error.message.slice(0, 60)}
                    {error.message.length > 60 && '...'}
                  </span>
                </TableCell>
                <TableCell>
                  {error.resolved ? (
                    <span className={styles.resolved}>Resolved</span>
                  ) : (
                    <span className={styles.unresolved}>Open</span>
                  )}
                </TableCell>
                <TableCell>
                  <button
                    className={styles.viewBtn}
                    onClick={() => setSelectedError(error)}
                  >
                    View
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {isLoading && <div className={styles.loading}>Loading...</div>}
        {!isLoading && !data?.errors.length && (
          <div className={styles.empty}>No errors found</div>
        )}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span className={styles.pageInfo}>
            Page {page + 1} of {totalPages}
          </span>
          <button
            className={styles.pageBtn}
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Purge Confirmation Modal */}
      {showPurgeConfirm && (
        <div className={styles.modalOverlay} onClick={() => setShowPurgeConfirm(false)}>
          <Window
            title="Purge Errors"
            maxWidth={400}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <div className={styles.errorDetail}>
              <p style={{ color: '#c0c0c0', fontSize: 14, margin: 0 }}>
                This will permanently delete <strong style={{ color: '#ff4444' }}>all errors</strong> matching
                the current filters (across all pages, not just the current page).
              </p>
              <p style={{ color: '#808080', fontSize: 12, margin: '8px 0 0 0' }}>
                Filters: source={source || 'all'}, level={level || 'all'}, resolved={resolved || 'all'}
              </p>
              <div className={styles.detailActions}>
                <Button onClick={() => setShowPurgeConfirm(false)}>Cancel</Button>
                <Button
                  onClick={() => purgeMutation.mutate()}
                  loading={purgeMutation.isPending}
                >
                  Purge All
                </Button>
              </div>
            </div>
          </Window>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedError && (
        <div className={styles.modalOverlay} onClick={() => setShowDeleteConfirm(false)}>
          <Window
            title="Delete Error"
            maxWidth={400}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <div className={styles.errorDetail}>
              <p style={{ color: '#c0c0c0', fontSize: 14, margin: 0 }}>
                Are you sure you want to delete this error log?
              </p>
              <div className={styles.detailActions}>
                <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
                <Button
                  onClick={() => {
                    deleteMutation.mutate(selectedError.id);
                    setShowDeleteConfirm(false);
                  }}
                  loading={deleteMutation.isPending}
                >
                  Delete
                </Button>
              </div>
            </div>
          </Window>
        </div>
      )}

      {/* Error Detail Modal */}
      {selectedError && (
        <div className={styles.modalOverlay} onClick={() => setSelectedError(null)}>
          <Window
            title="Error Details"
            maxWidth={600}
            onClose={() => setSelectedError(null)}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <div className={styles.errorDetail}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>ID</span>
                <span className={styles.detailValue}>{selectedError.id}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Source</span>
                <span className={styles.detailValue}>{selectedError.source}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Level</span>
                <span className={`${styles.level} ${styles[`level${selectedError.level}`]}`}>
                  {selectedError.level}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Timestamp</span>
                <span className={styles.detailValue}>
                  {formatTimestamp(selectedError.created_at)}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>URL</span>
                <span className={styles.detailValue}>{selectedError.url || '-'}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>User Agent</span>
                <span className={styles.detailValue}>{selectedError.user_agent || '-'}</span>
              </div>
              <div className={styles.detailSection}>
                <span className={styles.detailLabel}>Message</span>
                <pre className={styles.detailPre}>{selectedError.message}</pre>
              </div>
              {selectedError.stack_trace && (
                <div className={styles.detailSection}>
                  <span className={styles.detailLabel}>Stack Trace</span>
                  <pre className={styles.detailPre}>{selectedError.stack_trace}</pre>
                </div>
              )}
              {selectedError.metadata && (
                <div className={styles.detailSection}>
                  <span className={styles.detailLabel}>Metadata</span>
                  <pre className={styles.detailPre}>
                    {JSON.stringify(selectedError.metadata, null, 2)}
                  </pre>
                </div>
              )}

              <div className={styles.detailActions}>
                <Button
                  onClick={() =>
                    resolveMutation.mutate({
                      id: selectedError.id,
                      resolved: !selectedError.resolved,
                    })
                  }
                  loading={resolveMutation.isPending}
                >
                  {selectedError.resolved ? 'Mark Unresolved' : 'Mark Resolved'}
                </Button>
                {hasRole('mod') && (
                  <Button
                    onClick={() => setShowDeleteConfirm(true)}
                    loading={deleteMutation.isPending}
                  >
                    Delete
                  </Button>
                )}
              </div>
            </div>
          </Window>
        </div>
      )}
    </div>
  );
}
